import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  sendMessage: (data: any) => void;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  typingUsers: { [conversationId: string]: string[] };
  onlineUsers: string[];
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

const getSocketUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Handle WebContainer environments
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002`;
    }

    // Render or production (same domain)
    return `${protocol}//${hostname}`;
  }

  // Fallback: use VITE_SOCKET_URL if explicitly defined
  return import.meta.env.VITE_SOCKET_URL || 'ws://localhost:5002';
};


export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<{ [conversationId: string]: string[] }>({});
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);

  useEffect(() => {
    if (user?._id) {
      const socketUrl = getSocketUrl();
      const newSocket = io(socketUrl, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      // Connection event handlers
      newSocket.on('connect', () => {
        setIsConnected(true);
        
        // Register user with socket
        newSocket.emit('register', user._id);
      });

      newSocket.on('disconnect', (reason) => {
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        setIsConnected(false);
      });

      newSocket.on('reconnect', (attemptNumber) => {
        setIsConnected(true);
        newSocket.emit('register', user._id);
      });

      // Typing indicators
      newSocket.on('user_typing', ({ userId, userName, conversationId }) => {
        setTypingUsers(prev => ({
          ...prev,
          [conversationId]: [...(prev[conversationId] || []).filter(id => id !== userId), userId]
        }));
      });

      newSocket.on('user_stopped_typing', ({ userId, conversationId }) => {
        setTypingUsers(prev => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).filter(id => id !== userId)
        }));
      });

      // Online status
      newSocket.on('user_online', (userId) => {
        setOnlineUsers(prev => [...prev.filter(id => id !== userId), userId]);
      });

      newSocket.on('user_offline', (userId) => {
        setOnlineUsers(prev => prev.filter(id => id !== userId));
      });

      // Message events
      newSocket.on('message_sent', (message) => {
      });

      newSocket.on('message_error', (error) => {
      });

      // Store socket globally for access in components
      (window as any).socket = newSocket;
      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
        (window as any).socket = null;
        setSocket(null);
        setIsConnected(false);
      };
    }
  }, [user?._id]);

  const sendMessage = (data: any) => {
    if (socket && isConnected) {
      socket.emit('send_message', {
        ...data,
        sender: user?._id,
        senderName: `${user?.firstName} ${user?.lastName}`,
      });
    } else {
    }
  };

  const joinConversation = (conversationId: string) => {
    if (socket && isConnected) {
      socket.emit('join_conversation', conversationId);
    }
  };

  const leaveConversation = (conversationId: string) => {
    if (socket && isConnected) {
      socket.emit('leave_conversation', conversationId);
    }
  };

  const startTyping = (conversationId: string) => {
    if (socket && isConnected && user) {
      socket.emit('start_typing', {
        conversationId,
        userId: user._id,
        userName: `${user.firstName} ${user.lastName}`
      });
    }
  };

  const stopTyping = (conversationId: string) => {
    if (socket && isConnected && user) {
      socket.emit('stop_typing', {
        conversationId,
        userId: user._id
      });
    }
  };

  const value: SocketContextType = {
    socket,
    isConnected,
    sendMessage,
    joinConversation,
    leaveConversation,
    startTyping,
    stopTyping,
    typingUsers,
    onlineUsers,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

